import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DailyQuote } from "../components/DailyQuote";
import { ProgressStats } from "../components/stats/ProgressStats";
import { MoodChart } from "../components/stats/MoodChart";
import { WeeklyHighlights } from "../components/stats/WeeklyHighlights";
import { NextChallenges } from "../components/stats/NextChallenges";
import { useJournal } from "../hooks/useJournal";
import { useGoalsStats } from "../hooks/useGoalsStats";
import { startOfWeek } from "date-fns";
import { es } from "date-fns/locale";
import { Target, TrendingUp, CheckCircle, AlertTriangle, Loader } from "lucide-react";
import StreakStats from "../components/stats/StreakStats";
import StreakDetailsModal from "../components/stats/StreakDetailsModal";
import { motion } from "framer-motion";
import { getMockQuote } from "../lib/utils";
import TimeRangeSelector, { TimeRange } from "../components/ui/TimeRangeSelector";
import QuickActions from "../components/QuickActions";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

export default function HomePage() {
  const { t } = useTranslation();

  const {
    entries,
    isLoading: journalLoading,
    error: journalError,
    fetchEntries
  } = useJournal();

  const {
    summaryStats,
    isLoading: statsLoading,
    error: statsError,
    fetchSummaryStats
  } = useGoalsStats();

  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [showStreakDetailsModal, setShowStreakDetailsModal] = useState(false);

  // Función para determinar el color de la racha basado en el nivel
  const getStreakColor = useCallback((currentStreak: number) => {
    if (currentStreak >= 90) return 'purple';
    if (currentStreak >= 30) return 'green';
    if (currentStreak >= 7) return 'blue';
    return 'orange';
  }, []);

  // Daily quote selection
  const dailyQuote = getMockQuote();

  // Mapeo de iconos para los stats de resumen
  const iconMap = {
    'GoalIcon': Target,
    'CheckCircle': CheckCircle,
    'TrendingUp': TrendingUp,
    'AlertTriangle': AlertTriangle
  };

  // Cargar los datos de journal
  useEffect(() => {
    const selectedDate = new Date();
    const weekStart = startOfWeek(selectedDate, { locale: es });
    fetchEntries(weekStart);
  }, [fetchEntries]);

  // Cargar estadísticas de objetivos
  useEffect(() => {
    fetchSummaryStats(timeRange as any);
  }, [fetchSummaryStats, timeRange]);

  // Filtrar solo las estadísticas que queremos mostrar en la página principal
  const displayStats = summaryStats.slice(0, 2);

  // Simplificamos este manejador para usar el componente
  const handleTimeRangeChange = (range: any) => {
    setTimeRange(range);
    fetchSummaryStats(range);
  };

  const openStreakDetailsModal = () => {
    setShowStreakDetailsModal(true);
  };

  const closeStreakDetailsModal = () => {
    setShowStreakDetailsModal(false);
  };

  useEffect(() => {
    fetchSummaryStats(timeRange as any);
  }, []);

  // Mapear el rango de tiempo actual a la propiedad timeRange esperada por el componente StreakStats
  const mapTimeRangeForStreak = (): 'week' | 'month' | 'year' | 'all' => {
    switch (timeRange) {
      case 'day': return 'week';
      case 'week': return 'month';
      case 'month':
      default: return 'all';
    }
  };

  return (
    <motion.div
      className="container mx-auto px-4 py-6 space-y-6 max-w-7xl"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Daily Inspiration */}
      <motion.div variants={itemVariants}>
        <DailyQuote quote={dailyQuote} />
      </motion.div>

      {/* <motion.div variants={itemVariants}>
        <TimeRangeSelector
          value={timeRange}
          onChange={handleTimeRangeChange}
          availableRanges={['day', 'week', 'month']}
          align="right"
          throttleTime={1000}
          maxChangesCount={5}
          timeWindow={10000}
          blockTime={20000}
        />
      </motion.div> */}

      {/* Goals and Progress */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        variants={itemVariants}
      >
        {statsLoading && !displayStats.length ? (
          <div className="col-span-2 flex items-center justify-center h-32">
            <Loader className="w-6 h-6 text-blue-500 animate-spin" />
            <span className="ml-2 text-gray-500">{t('common.loading')}</span>
          </div>
        ) : statsError && !displayStats.length ? (
          <div className="col-span-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <p>{statsError}</p>
          </div>
        ) : (
          displayStats.map((stat, index) => (
            <ProgressStats
              key={`stat-${index}`}
              title={stat.title}
              value={stat.value}
              label={stat.label}
              trend={stat.trend}
              color={stat.color as any}
              progress={stat.progress}
              icon={iconMap[stat.icon as keyof typeof iconMap]}
              timeRange={stat.timeRange}
              comparison={stat.comparison}
              pulseEffect={stat.pulseEffect}
              onRefresh={() => fetchSummaryStats(timeRange as any)}
              refreshCooldown={30000}
              maxRefreshCount={3}
            />
          ))
        )}
      </motion.div>

      {/* Streak Statistics */}
      <motion.div variants={itemVariants}>
        <StreakStats
          timeRange={mapTimeRangeForStreak()}
          autoRefresh={true}
          refreshInterval={300000} // 5 minutos
          onDetailsClick={openStreakDetailsModal}
          color="red" // se puede usar getStreakColor
          streakLevels={[
            { threshold: 0, label: t('streakStats.levels.beginner') },
            { threshold: 7, label: t('streakStats.levels.consistent') },
            { threshold: 30, label: t('streakStats.levels.dedicated') },
            { threshold: 90, label: t('streakStats.levels.master') }
          ]}
          refreshCooldown={10000} // 10 segundos entre refrescos
          maxRefreshCount={3} // máximo 3 refrescos
          refreshTimeWindow={60000} // en un periodo de 1 minuto
        />
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={itemVariants}>
        <QuickActions />
      </motion.div>

      {/* Mood and Insights Section */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
        variants={itemVariants}
      >
        {/* Mood Tracking */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
          variants={itemVariants}
        >
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            {t('home.moodChart.title')}
          </h2>
          <MoodChart entries={entries as any} />
        </motion.div>

        {/* Highlights and Challenges */}
        <motion.div
          className="space-y-6 mb-10"
          variants={itemVariants}
        >
          <WeeklyHighlights />
          {/* <NextChallenges /> */}
        </motion.div>
      </motion.div>

      {/* Modal para detalles de racha */}
      {showStreakDetailsModal && (
        <StreakDetailsModal
          timeRange={mapTimeRangeForStreak()}
          onClose={closeStreakDetailsModal}
        />
      )}
    </motion.div>
  );
}